/**
 * Licitação: leitura do número de controle do PNCP e importação de itens de planilha (17 §3.2; D34, D71, D72).
 * Regras puras, sem acesso a banco ou rede.
 */

export interface ControlePncp {
  cnpj: string;
  ano: number;
  sequencial: number;
  /** Formato oficial: "CNPJ-1-SEQUENCIAL/ANO" (sequencial com 6 dígitos). */
  numero: string;
}

function montar(cnpj: string, ano: string, sequencial: string): ControlePncp | null {
  const a = Number(ano);
  const s = Number(sequencial);
  if (cnpj.length !== 14 || !Number.isInteger(a) || a < 2000 || a > 2100 || !Number.isInteger(s) || s <= 0) return null;
  return { cnpj, ano: a, sequencial: s, numero: `${cnpj}-1-${String(s).padStart(6, "0")}/${a}` };
}

/**
 * Número de controle do PNCP a partir do que a pessoa colar: o próprio número ("10825373000155-1-000177/2025")
 * ou o link da página do edital ("https://pncp.gov.br/app/editais/10825373000155/2025/177").
 */
export function lerControlePncp(texto: string): ControlePncp | null {
  const t = texto.trim();
  const numero = t.match(/(\d{14})-\d-(\d{1,6})\/(\d{4})/);
  if (numero?.[1] && numero[2] && numero[3]) return montar(numero[1], numero[3], numero[2]);
  const link = t.match(/\/(\d{14})\/(\d{4})\/(\d{1,6})(?:[/?#]|$)/);
  if (link?.[1] && link[2] && link[3]) return montar(link[1], link[2], link[3]);
  return null;
}

/** Benefício ME/EPP do item pelo código "tipoBeneficio" do PNCP. */
export function beneficioDoPncp(codigo: number | null | undefined): "exclusivo" | "subcontratacao" | "cota_reservada" | "nenhum" | null {
  switch (codigo) {
    case 1:
      return "exclusivo";
    case 2:
      return "subcontratacao";
    case 3:
      return "cota_reservada";
    case 4:
    case 5:
      return "nenhum";
    default:
      return null;
  }
}

/* ------------------------------ Importação de itens por planilha ------------------------------ */

export const CAMPOS_ITEM = ["lote", "numero", "descricao", "qtd_min", "qtd_max", "unidade", "valor_unit_estimado"] as const;
export type CampoItem = (typeof CAMPOS_ITEM)[number];
export type Mapeamento = Partial<Record<CampoItem, number>>;

const normalizar = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9%]+/g, " ")
    .trim();

/** Palavras do cabeçalho que indicam cada campo (a mais específica vence). */
const PISTAS: [CampoItem, RegExp][] = [
  ["valor_unit_estimado", /\b(valor|preco|vlr|vl)\b.*\b(unit|unitario|unid)\b|\b(unit|unitario)\b.*\b(valor|preco)\b|valor (de )?referencia|preco (medio|estimado)|valor estimado/],
  ["qtd_min", /\b(qtd|quant|quantidade)\b.*\b(min|minima)\b/],
  ["qtd_max", /\b(qtd|quant|quantidade|qtde)\b/],
  ["unidade", /\b(unidade|und|un|unid|medida|u m|um)\b/],
  ["descricao", /\b(descricao|especificacao|objeto|produto|material|discriminacao)\b/],
  ["lote", /\b(lote|grupo)\b/],
  ["numero", /\b(item|n|no|numero|nº|seq)\b/],
];

/** Sugestão de mapeamento pelos nomes das colunas do cabeçalho; cada coluna vai para no máximo um campo. */
export function sugerirMapeamento(cabecalho: string[]): Mapeamento {
  const usado = new Set<number>();
  const mapa: Mapeamento = {};
  for (const [campo, pista] of PISTAS) {
    const indice = cabecalho.findIndex((c, i) => !usado.has(i) && pista.test(normalizar(c)) && !/total/.test(normalizar(c)));
    if (indice >= 0) {
      mapa[campo] = indice;
      usado.add(indice);
    }
  }
  return mapa;
}

/** Linha do cabeçalho: a primeira, entre as 30 iniciais, que sugere descrição e quantidade. */
export function encontrarCabecalho(linhas: unknown[][]): number {
  for (let i = 0; i < Math.min(30, linhas.length); i++) {
    const mapa = sugerirMapeamento((linhas[i] ?? []).map((c) => String(c ?? "")));
    if (mapa.descricao !== undefined && mapa.qtd_max !== undefined) return i;
  }
  return 0;
}

/**
 * Número de célula de planilha: número nativo ou texto no formato brasileiro ("1.234,56"), com ou sem "R$".
 * Devolve null para célula vazia e NaN para texto que não é número.
 */
export function numeroDaCelula(valor: unknown): number | null {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : Number.NaN;
  let t = String(valor).replace(/R\$|\s/gi, "").trim();
  if (t === "" || t === "-") return null;
  if (/^-?[\d.]+,\d+$/.test(t) || /^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, "").replace(",", ".");
  else if (/^-?\d+,\d+$/.test(t)) t = t.replace(",", ".");
  else if (/^-?[\d,]+\.\d+$/.test(t)) t = t.replace(/,/g, "");
  const n = Number(t);
  return Number.isFinite(n) ? n : Number.NaN;
}

export interface ItemImportado {
  linha: number;
  lote: string | null;
  numero: number | null;
  descricao: string;
  qtd_min: number | null;
  qtd_max: number | null;
  unidade: string | null;
  valor_unit_estimado: number | null;
  erros: string[];
}

const texto = (v: unknown) => (v === null || v === undefined ? "" : String(v).replace(/\s+/g, " ").trim());

/**
 * Converte as linhas abaixo do cabeçalho em itens, com os erros de cada um. Linhas sem descrição e sem quantidade
 * são ignoradas: separadores e, na planilha de preço da empresa, os itens do edital em que não se participa
 * (numeração mantida, só "UND" e a fórmula de total; SERGAS 22-2026, SEMFAS 64-2026).
 * Item sem número recebe a sequência da planilha (anterior + 1) — o erro só aparece se não houver como numerar.
 */
export function lerItens(linhas: unknown[][], linhaCabecalho: number, mapa: Mapeamento): ItemImportado[] {
  const celula = (linha: unknown[], campo: CampoItem) => (mapa[campo] === undefined ? undefined : linha[mapa[campo]]);
  const itens: ItemImportado[] = [];
  let ultimoNumero = 0;
  for (let i = linhaCabecalho + 1; i < linhas.length; i++) {
    const linha = linhas[i] ?? [];
    if (linha.every((c) => texto(c) === "")) continue;
    const descricao = texto(celula(linha, "descricao"));
    // Rodapé de total ("TOTAL GERAL", "Valor global") não é item.
    const semQuantidade = texto(celula(linha, "qtd_max")) === "";
    if (!descricao && semQuantidade) {
      // O número dela conta para a sequência dos itens sem número que vierem depois.
      const pulado = numeroDaCelula(celula(linha, "numero"));
      if (pulado !== null && Number.isInteger(pulado) && pulado > 0) ultimoNumero = pulado;
      continue;
    }
    if (/^(valor )?(total|global|subtotal)\b/i.test(normalizar(descricao)) && semQuantidade) continue;

    const erros: string[] = [];
    const numeroLido = numeroDaCelula(celula(linha, "numero"));
    let numero: number | null = null;
    if (numeroLido === null) numero = ultimoNumero + 1;
    else if (Number.isInteger(numeroLido) && numeroLido > 0) numero = numeroLido;
    else erros.push("número do item inválido");
    if (numero !== null) ultimoNumero = numero;

    const qtdMax = numeroDaCelula(celula(linha, "qtd_max"));
    const qtdMin = numeroDaCelula(celula(linha, "qtd_min"));
    const valor = numeroDaCelula(celula(linha, "valor_unit_estimado"));
    if (!descricao) erros.push("sem descrição");
    if (qtdMax === null) erros.push("sem quantidade");
    else if (Number.isNaN(qtdMax) || qtdMax <= 0) erros.push("quantidade inválida");
    if (qtdMin !== null && (Number.isNaN(qtdMin) || qtdMin < 0)) erros.push("quantidade mínima inválida");
    if (qtdMin !== null && qtdMax !== null && !Number.isNaN(qtdMin) && !Number.isNaN(qtdMax) && qtdMin > qtdMax) {
      erros.push("quantidade mínima maior que a máxima");
    }
    if (valor !== null && (Number.isNaN(valor) || valor < 0)) erros.push("valor unitário inválido");

    const lote = texto(celula(linha, "lote"));
    const unidade = texto(celula(linha, "unidade"));
    itens.push({
      linha: i + 1,
      lote: lote || null,
      numero,
      descricao,
      qtd_min: qtdMin === null || Number.isNaN(qtdMin) ? null : qtdMin,
      qtd_max: qtdMax === null || Number.isNaN(qtdMax) ? null : qtdMax,
      unidade: unidade || null,
      valor_unit_estimado: valor === null || Number.isNaN(valor) ? null : valor,
      erros,
    });
  }
  // Número repetido no mesmo lote.
  const vistos = new Map<string, number>();
  for (const item of itens) {
    if (item.numero === null) continue;
    const chave = `${item.lote ?? ""}#${item.numero}`;
    const anterior = vistos.get(chave);
    if (anterior !== undefined) item.erros.push(`item ${item.numero} repetido (linha ${anterior})`);
    else vistos.set(chave, item.linha);
  }
  return itens;
}

/** Linhas coladas do Excel (texto separado por tabulação). */
export function linhasColadas(textoColado: string): string[][] {
  const linhas = textoColado.replace(/\r\n?/g, "\n").split("\n");
  while (linhas.length > 0 && (linhas[linhas.length - 1] ?? "").trim() === "") linhas.pop();
  return linhas.map((l) => l.split("\t"));
}

/* ------------------------------ Busca: destaque das palavras-chave ------------------------------ */

/** Mesma normalização da busca no banco, letra a letra (mantém as posições do texto original). */
function normalizarLetra(c: string): string {
  const base = c.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  return base.length === 1 ? base : c.toLowerCase().slice(0, 1) || " ";
}

/**
 * Divide o texto em trechos, marcando os que casam com algum termo no início de uma palavra
 * (mesma regra de public.buscar_pncp: sem acento, sem diferenciar maiúsculas).
 */
export function trechosDestacados(texto: string, termos: string[]): { texto: string; destaque: boolean }[] {
  const normalizado = Array.from(texto).map(normalizarLetra).join("");
  const letras = Array.from(texto);
  const marcado = new Array<boolean>(letras.length).fill(false);
  for (const termoOriginal of termos) {
    const termo = Array.from(termoOriginal.trim()).map(normalizarLetra).join("");
    if (!termo) continue;
    let desde = 0;
    for (;;) {
      const i = normalizado.indexOf(termo, desde);
      if (i < 0) break;
      const anterior = i > 0 ? normalizado[i - 1] : " ";
      if (!anterior || !/[a-z0-9]/.test(anterior)) {
        // Destaca até o fim da palavra (o termo pode ser um radical: "eletronic" → "eletrônicos").
        let fim = i + termo.length;
        while (fim < letras.length && /[a-z0-9]/.test(normalizado[fim] ?? "")) fim++;
        for (let k = i; k < fim; k++) marcado[k] = true;
      }
      desde = i + 1;
    }
  }
  const trechos: { texto: string; destaque: boolean }[] = [];
  letras.forEach((letra, i) => {
    const ultimo = trechos[trechos.length - 1];
    if (ultimo && ultimo.destaque === marcado[i]) ultimo.texto += letra;
    else trechos.push({ texto: letra, destaque: Boolean(marcado[i]) });
  });
  return trechos;
}

/**
 * Resultado geral da licitação a partir dos itens disputados (12). Mesmas regras de privado.resultado_licitacao.
 * null = nenhum resultado registrado; "pendente" = há item disputado sem resultado.
 */
export function resultadoGeral(situacoes: (string | null)[]): string | null {
  if (situacoes.length === 0 || situacoes.every((s) => s === null)) return null;
  if (situacoes.some((s) => s === null)) return "pendente";
  if (situacoes.every((s) => s === "ganho")) return "ganha";
  if (situacoes.some((s) => s === "ganho")) return "ganha_em_parte";
  if (situacoes.every((s) => s === "fracassado")) return "fracassada";
  if (situacoes.every((s) => s === "deserto")) return "deserta";
  return "perdida";
}
